import { ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useGuide } from './useGuide'

export function useEnrichIpGuide() {
  const router = useRouter()
  const route = useRoute()
  const isActive = ref(false)
  const { start: startGuide, cancel: cancelGuide, _rawState: state } = useGuide()

  function start() {
    const steps = [
      {
        id: 'welcome',
        text: 'This guide walks you through enriching an IP address. You\'ll submit an IP and learn how to read the results.',
        buttons: [
          { text: 'Skip', action: 'cancel' },
          { text: "Let's go", action: 'next', primary: true },
        ],
      },
      {
        id: 'sidebar-enrich',
        text: 'The <strong>Enrich</strong> page is where you submit IPs and domains for threat intelligence lookups. Click it or press Next to navigate there.',
        attachTo: { element: '[data-guide="sidebar-enrich"]', on: 'right' },
        canClickTarget: true,
        requireRoute: '/enrich',
        onNext() {
          router.push('/enrich')
        },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'ip-tab',
        text: 'Make sure the <strong>IP Addresses</strong> tab is selected. You can also enrich domains from the other tab.',
        attachTo: { element: '[data-guide="enrich-ip-tab"]', on: 'bottom' },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
        onShow() {
          const ipTab = document.querySelector('[data-guide="enrich-ip-tab"]')
          if (ipTab) ipTab.click()
        },
      },
      {
        id: 'textarea',
        text: 'Type an IP address into the field — for example <code>8.8.8.8</code>.',
        attachTo: { element: '[data-guide="enrich-ip-textarea"]', on: 'bottom' },
        watchInput: {
          selector: '[data-guide="enrich-ip-textarea"]',
          value: '8.8.8.8',
        },
        canClickTarget: true,
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'submit',
        text: 'Click <strong>Submit</strong> to enrich the IP. The platform will query all your enabled sources and compile the results.',
        attachTo: { element: '[data-guide="enrich-ip-submit"]', on: 'top' },
        canClickTarget: true,
        requireRoute: '/enrich/ip',
        onNext() {
          const btn = document.querySelector('[data-guide="enrich-ip-submit"]')
          if (btn) btn.click()
        },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'ip-header',
        text: 'The header shows the IP address, its geolocation, and an <strong>Open in</strong> menu to pivot into external tools like Shodan, VirusTotal, and AbuseIPDB.',
        attachTo: { element: '[data-guide="ip-header"]', on: 'bottom' },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'ip-cards',
        text: 'These cards summarize the enrichment results — <strong>Infrastructure</strong> shows ASN, network range, and provider info. <strong>OSINT</strong> and <strong>Anonymization</strong> cards appear when sources flag the IP.',
        attachTo: { element: '[data-guide="ip-cards"]', on: 'bottom' },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'ip-netflow',
        text: 'The <strong>Netflow observations</strong> histogram shows every event that mentions this IP over time. Click any bar to drill into the raw events for that time window, or brush-select a range.',
        attachTo: { element: '[data-guide="ip-netflow"]', on: 'bottom' },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Next', action: 'next', primary: true },
        ],
      },
      {
        id: 'ip-connections',
        text: 'The <strong>Network connections</strong> chart maps direct source and destination traffic — IPs on the outside, ports in the middle, and your IP at the center. Click any node or edge to pivot into the underlying events.',
        attachTo: { element: '[data-guide="ip-connections"]', on: 'top' },
        buttons: [
          { text: 'Back', action: 'back' },
          { text: 'Finish', action: 'complete', primary: true },
        ],
      },
    ]

    isActive.value = true
    startGuide('enrich-ip', steps)

    // Watch for guide deactivation
    const stopWatching = watch(
      () => state.active,
      (active) => {
        if (!active) {
          isActive.value = false
          stopWatching()
        }
      }
    )
  }

  return { start, isActive }
}
